import { db, type DiaperEntry, type WeightEntry } from '../db';

function toPlain<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export async function logDiaper(
  childId: string,
  type: DiaperEntry['type'],
  note?: string,
): Promise<DiaperEntry> {
  const now = Date.now();
  const entry: DiaperEntry = {
    id: crypto.randomUUID(),
    childId,
    type,
    time: now,
    note,
    createdAt: now,
    updatedAt: now,
  };
  await db.diapers.add(toPlain(entry));
  return entry;
}

export async function getDiapersByDay(childId: string, date: number): Promise<DiaperEntry[]> {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const diapers = await db.diapers
    .where('[childId+time]')
    .between([childId, dayStart.getTime()], [childId, dayEnd.getTime()])
    .toArray();

  return diapers.filter((d) => !d.deletedAt);
}

export async function getDiaperCountToday(
  childId: string,
): Promise<{ wet: number; dirty: number; total: number }> {
  const today = await getDiapersByDay(childId, Date.now());
  const wet = today.filter((d) => d.type === 'wet' || d.type === 'both').length;
  const dirty = today.filter((d) => d.type === 'dirty' || d.type === 'both').length;
  return { wet, dirty, total: today.length };
}

export async function addWeight(
  childId: string,
  weightGrams: number,
  date?: number,
  note?: string,
): Promise<WeightEntry> {
  const now = Date.now();
  const entry: WeightEntry = {
    id: crypto.randomUUID(),
    childId,
    weightGrams,
    date: date || now,
    note,
    createdAt: now,
    updatedAt: now,
  };
  await db.weights.add(toPlain(entry));
  return entry;
}

export async function getWeightHistory(childId: string): Promise<WeightEntry[]> {
  const entries = await db.weights
    .where('[childId+date]')
    .between([childId, 0], [childId, Number.MAX_SAFE_INTEGER])
    .toArray();
  return entries.filter((w) => !w.deletedAt);
}

export async function getRecentDiapers(
  childId: string,
  limit: number = 20,
): Promise<DiaperEntry[]> {
  const all = await db.diapers
    .where('childId')
    .equals(childId)
    .reverse()
    .sortBy('time');
  return all.filter((d) => !d.deletedAt).slice(0, limit);
}
